import { Clock, History, Search, UserCheck } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Students Real Screen 1. Replaces TutorShowcaseSection: there is no public tutor directory and no
 * consenting-tutor roster to show, and the product matches rather than lists. Shows the real
 * request flow instead (RequestSessionPage): subject, details, optional previous tutor.
 */
function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold">{label}</p>
      <div className="rounded-md border bg-background px-3 py-2 text-sm">{children}</div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function StatusRow({ tone, title, body }: { tone: 'wait' | 'go'; title: string; body: string }) {
  return (
    <li className="flex items-start gap-3 rounded-lg border bg-background p-3">
      <span
        className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${tone === 'go' ? 'bg-amber-500' : 'bg-blue-400'}`}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
    </li>
  );
}

export function TutorRequestSection() {
  return (
    <RealScreen
      id="section-tutors"
      audience="students"
      layout="band"
      heading="Tell us what you need. We find the tutor."
      lede="You ask for a subject and a goal. Mentara matches you with an available tutor, and they propose a time."
      callouts={[
        { icon: Search, title: 'Say what you need', body: 'Pick a subject and describe what you are working on. More detail means a better match.' },
        { icon: History, title: 'Ask for a tutor you know', body: 'After your first session, you can request the same tutor again.' },
        { icon: UserCheck, title: 'A tutor proposes a time', body: 'You accept it or suggest another. Nothing is booked until you both agree.' },
      ]}
      caption="Example session request."
    >
      <div className="grid gap-6 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <p className="text-base font-semibold">Request a tutoring session</p>
          <Field label="Subject">SAT Math</Field>
          <Field label="What do you need help with?">
            Algebra word problems. I keep losing points on the harder ones, and my test is in six weeks.
          </Field>
          <Field label="Request a previous tutor (optional)" hint="Shown once you have finished a session.">
            No preference (assign any tutor)
          </Field>
          <div className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground" aria-hidden>
            Send request
          </div>
        </div>
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-base font-semibold">
            <Clock className="h-4 w-4 text-muted-foreground" aria-hidden />
            What happens next
          </p>
          <ul className="space-y-2">
            <StatusRow tone="wait" title="Finding a tutor…" body="Mentara matches your request with an available tutor." />
            <StatusRow tone="go" title="Tutor proposed a time" body="Accept it, or counter-propose another." />
          </ul>
        </div>
      </div>
    </RealScreen>
  );
}
